import { Avatar, Box, Button, Chip, Stack, Typography } from "@mui/material";
import moment from "moment";
import EditIcon from "@mui/icons-material/Edit";
import CustomImage from "../../components/CustomImage";

const renderPreview = (src?: string, fallback?: string) =>
    src ? (
        <CustomImage
            src={src}
            style={{ width: 48, height: 32, borderRadius: 6, objectFit: 'cover' }}
        />
    ) : (
        <Avatar sx={{ width: 48, height: 32, borderRadius: 1 }} variant="rounded">
            {fallback?.charAt(0) || '-'}
        </Avatar>
    );

export const getColumns = (
    currentPage: number,
    rows: number,
    onEdit?: (row: any) => void
) => [
    {
        header: "S.No.",
        accessor: "_index",
        render: (_row: any, index?: number) => (currentPage - 1) * rows + (index ?? 0) + 1,
    },
    {
        header: "Cover",
        accessor: "coverImage",
        render: (row: any) => renderPreview(row.coverImage, row.name || row.providerName),
    },
    {
        header: "Image",
        accessor: "image",
        render: (row: any) => renderPreview(row.image, row.name || row.providerName),
    },
    {
        header: "Name",
        accessor: "name",
        render: (row: any) => (
            <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {row.name || '-'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Provider: {row.providerName || '-'}
                </Typography>
            </Box>
        ),
    },
    {
        header: "External ID",
        accessor: "externalItemId",
    },
    {
        header: "Provider Price",
        accessor: "price",
        render: (row: any) => row.price != null ? `Rs. ${row.price}` : '-',
    },
    {
        header: "Custom Price",
        accessor: "customPrice",
        render: (row: any) => row.customPrice != null ? `Rs. ${row.customPrice}` : '-',
    },
    {
        header: "Custom MRP",
        accessor: "customMrp",
        render: (row: any) => row.customMrp != null ? `Rs. ${row.customMrp}` : '-',
    },
    {
        header: "Markup",
        accessor: "markupPercentage",
        render: (row: any) => {
            if (row.markupPercentage > 0) return `${row.markupPercentage}%`;
            if (row.markupAmount > 0) return `+Rs. ${row.markupAmount}`;
            return '-';
        },
    },
    {
        header: "Gender",
        accessor: "gender",
        render: (row: any) => row.itemType === 'package' && row.gender
            ? row.gender.replace(/^./, (value: string) => value.toUpperCase())
            : 'All',
    },
    {
        header: "Applicable Age",
        accessor: "applicableAge",
        render: (row: any) => row.itemType === 'package' && row.applicableAge != null
            ? `${row.applicableAge}+ years`
            : 'All ages',
    },
    {
        header: "Camp",
        accessor: "campIds",
        render: (row: any) => {
            if (row.itemType !== 'package') return '-';
            const campIds = Array.isArray(row.campIds) && row.campIds.length
                ? row.campIds
                : row.campId
                    ? [row.campId]
                    : [];
            return campIds.length ? campIds.join(', ') : '-';
        },
    },
    {
        header: "Pinned",
        accessor: "pinned",
        render: (row: any) => (
            <Chip
                label={row.pinned ? "Pinned" : "Normal"}
                color={row.pinned ? "primary" : "default"}
                size="small"
            />
        ),
    },
    {
        header: "Review",
        accessor: "status",
        render: (row: any) => (
            <Chip
                label={row.status === 2 ? "Approved" : "New"}
                color={row.status === 2 ? "success" : "warning"}
                size="small"
            />
        ),
    },
    {
        header: "Visibility",
        accessor: "enabled",
        render: (row: any) => (
            <Chip
                label={row.enabled ? "Enabled" : "Disabled"}
                color={row.enabled ? "success" : "default"}
                size="small"
            />
        ),
    },
    {
        header: "Updated",
        accessor: "updatedAt",
        render: (row: any) => row.updatedAt ? moment(row.updatedAt).format("DD-MM-YYYY") : "-",
    },
    ...(onEdit ? [{
        header: "Edit",
        accessor: "edit",
        render: (row: any) => (
            <Stack direction="row" spacing={1}>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => onEdit(row)}
                >
                    Edit
                </Button>
            </Stack>
        ),
    }] : []),
];
